import React, { useState, useEffect } from 'react';
import StoreDetails from './StoreDetails';
import AITestingTool from './AITestingTool';
import OrderApprovalSettings from './OrderApprovalSettings';
import PNLCategoriesSettings from './PNLCategoriesSettings';
import EmailDeliverySettings from './EmailDeliverySettings';
import CloverSettings from './CloverSettings';
import supabase from '../config/supabase';

const Settings = ({ onCancel, initialActiveTab }) => {
  const [activeSettingsTab, setActiveSettingsTab] = useState(initialActiveTab || 'store-details');
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUser(user);
        console.log('Settings - Current user:', user);
        console.log('Settings - User metadata:', user.user_metadata);

        // Create user profile from auth metadata (no database table needed)
        setUserProfile({
          id: user.id,
          email: user.email,
          first_name: user.user_metadata?.first_name || '',
          last_name: user.user_metadata?.last_name || '',
          phone: user.user_metadata?.phone || '',
          full_name: user.user_metadata?.full_name || ''
        });

        console.log('Settings - User profile created from auth data');
      }
    } catch (error) {
      console.error('Settings - Error loading user data:', error);
    }
  };

  const renderSettingsContent = () => {
    switch (activeSettingsTab) {
      case 'store-details':
        return (
          <StoreDetails 
            onSave={(data) => {
              console.log('Store details saved:', data);
              // Stay on the same tab after saving
            }}
            onCancel={onCancel}
          />
        );
      case 'user-profile':
        return (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">User Profile</h5>
            </div>
            <div className="card-body">
              {currentUser ? (
                <div>
                  <div className="alert alert-info mb-4">
                    <h6>Current User Information</h6>
                    <p className="mb-0">This information comes from your Supabase authentication profile (no database table required).</p>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">First Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={userProfile?.first_name || ''}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Last Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={userProfile?.last_name || ''}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        className="form-control"
                        value={currentUser.email || ''}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <input
                        type="text"
                        className="form-control"
                        value={userProfile?.phone || ''}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">User ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={currentUser.id || ''}
                        readOnly
                        style={{ fontSize: '12px', fontFamily: 'monospace' }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Account Created</label>
                      <input
                        type="text"
                        className="form-control"
                        value={currentUser.created_at ? new Date(currentUser.created_at).toLocaleDateString() : ''}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="alert alert-success mb-3">
                    <h6>✅ Available User Data</h6>
                    <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                      <div><strong>Email:</strong> {currentUser.email || 'Not available'}</div>
                      <div><strong>User ID:</strong> {currentUser.id || 'Not available'}</div>
                      <div><strong>Created:</strong> {currentUser.created_at ? new Date(currentUser.created_at).toLocaleString() : 'Not available'}</div>
                      {currentUser.user_metadata && Object.keys(currentUser.user_metadata).length > 0 && (
                        <div><strong>Metadata:</strong> {JSON.stringify(currentUser.user_metadata, null, 2)}</div>
                      )}
                    </div>
                  </div>

                  <div className="alert alert-warning">
                    <h6>🔧 Session Name Preview</h6>
                    <p className="mb-2">This is how your name will appear in auto-generated session names:</p>
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      fontSize: '14px'
                    }}>
                      {new Date().toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: '2-digit'
                      })} - Template Name - {
                        currentUser?.email ||
                        userProfile?.full_name ||
                        (userProfile?.first_name && userProfile?.last_name ? `${userProfile.first_name} ${userProfile.last_name}` : '') ||
                        userProfile?.first_name ||
                        'User'
                      }
                    </div>
                  </div>

                  <div className="card mt-4" style={{ borderColor: '#EAC4C9', backgroundColor: '#FCEEF0' }}>
                    <div className="card-body">
                      <h6 style={{ color: '#B03A48' }}>Delete account</h6>
                      <p className="mb-3">Permanently delete your account, profile, and store memberships. This can’t be undone.</p>
                      <a
                        href="/delete-account.html"
                        className="btn btn-danger"
                      >
                        Delete account
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="alert alert-warning">
                  <p className="mb-0">Loading user information...</p>
                </div>
              )}
            </div>
          </div>
        );
      case 'api-settings':
        return (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">AI API Settings</h5>
            </div>
            <div className="card-body">
              <div className="alert alert-success">
                <h6><i className="feather icon-check-circle me-2"></i>OpenAI API Configured</h6>
                <p className="mb-0">AI-powered categorization and location assignment is ready for use.</p>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">AI Service Provider</label>
                  <select className="form-control">
                    <option selected>OpenAI GPT-4</option>
                    <option disabled>Claude (Coming Soon)</option>
                    <option disabled>Custom API (Coming Soon)</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">API Status</label>
                  <div className="form-control bg-light">
                    <i className="feather icon-check text-success me-2"></i>
                    Connected & Ready
                  </div>
                </div>
              </div>
              <hr />
              <h6>AI Features</h6>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="autoCategory" checked />
                    <label className="form-check-label" htmlFor="autoCategory">
                      Auto-categorize items
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="autoLocation" checked />
                    <label className="form-check-label" htmlFor="autoLocation">
                      Auto-assign locations
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="smartVendor" checked />
                    <label className="form-check-label" htmlFor="smartVendor">
                      Smart vendor detection
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="pricePredict" disabled />
                    <label className="form-check-label" htmlFor="pricePredict">
                      Price prediction (Coming Soon)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'ai-testing':
        return <AITestingTool onClose={() => setActiveSettingsTab('api-settings')} />;
      case 'notifications':
        return (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Notifications</h5>
            </div>
            <div className="card-body">
              <div className="alert alert-info">
                <h6>Notification Preferences</h6>
                <p className="mb-0">Configure alerts for low stock, new inventory, and system updates.</p>
              </div>
              <div className="form-check mb-3">
                <input className="form-check-input" type="checkbox" id="lowStockAlerts" disabled />
                <label className="form-check-label" htmlFor="lowStockAlerts">
                  Low Stock Alerts
                </label>
              </div>
              <div className="form-check mb-3">
                <input className="form-check-input" type="checkbox" id="inventoryUpdates" disabled />
                <label className="form-check-label" htmlFor="inventoryUpdates">
                  Inventory Update Notifications
                </label>
              </div>
            </div>
          </div>
        );
      case 'order-approval':
        return <OrderApprovalSettings />;
      case 'pnl-categories':
        return <PNLCategoriesSettings />;
      case 'email-delivery':
        return <EmailDeliverySettings />;
      case 'clover':
        return (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Clover Integration</h5>
            </div>
            <div className="card-body">
              <CloverSettings />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="settings">
      <div className="row">
        {/* Settings Navigation Sidebar */}
        <div className="col-md-3">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">Settings</h6>
            </div>
            <div className="list-group list-group-flush">
              <button
                className={`list-group-item list-group-item-action ${activeSettingsTab === 'store-details' ? 'active' : ''}`}
                onClick={() => setActiveSettingsTab('store-details')}
              >
                <i className="feather icon-home me-2"></i>
                Store Details
              </button>
              <button
                className={`list-group-item list-group-item-action ${activeSettingsTab === 'user-profile' ? 'active' : ''}`}
                onClick={() => setActiveSettingsTab('user-profile')}
              >
                <i className="feather icon-user me-2"></i>
                User Profile
              </button>
              <button
                className={`list-group-item list-group-item-action ${activeSettingsTab === 'api-settings' ? 'active' : ''}`}
                onClick={() => setActiveSettingsTab('api-settings')}
              >
                <i className="feather icon-cpu me-2"></i>
                AI API Settings
              </button>
              <button
                className={`list-group-item list-group-item-action ${activeSettingsTab === 'ai-testing' ? 'active' : ''}`}
                onClick={() => setActiveSettingsTab('ai-testing')}
              >
                <i className="feather icon-zap me-2"></i>
                AI Testing Tool
              </button>
              <button
                className={`list-group-item list-group-item-action ${activeSettingsTab === 'notifications' ? 'active' : ''}`}
                onClick={() => setActiveSettingsTab('notifications')}
              >
                <i className="feather icon-bell me-2"></i>
                Notifications
              </button>
              <button
                className={`list-group-item list-group-item-action ${activeSettingsTab === 'order-approval' ? 'active' : ''}`}
                onClick={() => setActiveSettingsTab('order-approval')}
              >
                <i className="feather icon-mail me-2"></i>
                Order Approval
              </button>
              <button
                className={`list-group-item list-group-item-action ${activeSettingsTab === 'pnl-categories' ? 'active' : ''}`}
                onClick={() => setActiveSettingsTab('pnl-categories')}
              >
                <i className="feather icon-bar-chart-2 me-2"></i>
                P&amp;L Category Mapping
              </button>
              <button
                className={`list-group-item list-group-item-action ${activeSettingsTab === 'email-delivery' ? 'active' : ''}`}
                onClick={() => setActiveSettingsTab('email-delivery')}
              >
                <i className="feather icon-send me-2"></i>
                Email Delivery
              </button>
              <button
                className={`list-group-item list-group-item-action ${activeSettingsTab === 'clover' ? 'active' : ''}`}
                onClick={() => setActiveSettingsTab('clover')}
              >
                <i className="feather icon-link me-2"></i>
                Clover
              </button>
            </div>
            <div className="card-footer">
              <button 
                className="btn btn-secondary btn-sm w-100" 
                onClick={onCancel}
              >
                <i className="feather icon-arrow-left me-1"></i>
                Back to Inventory
              </button>
            </div>
          </div>
        </div>

        {/* Settings Content */}
        <div className="col-md-9">
          {renderSettingsContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;
